import KML from "ol/format/KML";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Polygon, { circular } from "ol/geom/Polygon";
import Style from "ol/style/Style";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";

import { ConvertCmpVolumesToFeatures, convertHexColorToRgbaColor } from "../../map/mapUtil";
import { DateHasAlreadyPassed, IsInvalidDate } from "../../util";

import polylabel from "polylabel";

import * as CONSTANT from "./opConstants";

export function getOperationPriority(op) {
    if (!op || typeof op.priority !== "number") {
        return "Unknown";
    }
    const priority = CONSTANT.FLIGHT_PRIORITIES.find(({ value }) => {
        return value === op.priority;
    });
    if (priority) {
        return `${priority.label}`;
    } else {
        return `${op.priority}`;
    }
}

export function getOperationEditable(operation, user) {
    const canEdit = canUserEditOrDelete(operation, user);
    const isConforming = operation.state !== "CONTINGENT" && operation.state !== "NONCONFORMING";
    const hasWaypoints = operationHasWaypoints(operation);
    const faaRequired = operation.faa_approval_required ? true : false;
    return canEdit && isConforming && !hasWaypoints && !faaRequired;
}

export function canUserEditOrDelete(operation, user) {
    const isExternal = operation.owner !== "cmp-ui-online" ? true : false;
    const isAdmin = user.user_role_id === 0 ? true : false;
    const isOwnOrg = operation.organization_id === user.organization_id ? true : false;
    const isOpManagerOrAirspaceManager = user.user_role_id === 1 || user.user_role_id === 4 ? true : false;
    const isOwnOp = user.id === operation.created_user_id ? true : false;

    if (isExternal) {
        return false;
    }
    if (isAdmin) {
        return true;
    }
    if (isOwnOrg) {
        return isOpManagerOrAirspaceManager ? true : isOwnOp;
    }
    return false;
}

export function operationHasWaypoints(operation) {
    if (Array.isArray(operation.waypoints) && operation.waypoints.length > 0) {
        return true;
    } else {
        return false;
    }
}

export function handleDownloadOperations(operations, settings) {
    const features = ConvertCmpVolumesToFeatures(operations, settings);

    const labels = features.map((feature) => {
        const coordinates = feature.getGeometry().getCoordinates();
        const position = polylabel(coordinates);

        return new Feature({
            name: feature.get("name"),
            geometry: new Point([position[0], position[1]])
        });
    });
    if (Array.isArray(features) && features.length > 0) {
        const format = new KML();
        const kml = format.writeFeatures([...features, ...labels]);

        const blob = new Blob([kml], { type: "kml" });
        const file = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = file;
        link.download = "operations.kml";
        document.body.appendChild(link);

        link.dispatchEvent(
            new MouseEvent("click", {
                bubbles: true,
                cancelable: true,
                view: window
            })
        );
        document.body.removeChild(link);
    } else {
        alert("Please select at least one operation before proceeding.");
    }
}

//checks to see if flight needs to show additional details.
export function showDetailsByState(state, laancOn) {
    if (laancOn === "false") {
        return false;
    }

    switch (state) {
        case CONSTANT.DECONFLICTION_CHECK_STATUS:
        case CONSTANT.DECONFLICTION_BLOCKED_STATUS:
        case CONSTANT.LAANC_REQUIRED_STATUS:
        case CONSTANT.LAANC_NOT_REQUIRED_STATUS:
        case CONSTANT.LAANC_BLOCKED_STATUS:
        case CONSTANT.LAANC_FURTHER_COORD_STATUS:
        case CONSTANT.LAANC_AUTO_APPROVED_STATUS:
        case CONSTANT.FAA_APPROVAL_CHECK_STATUS:
        case CONSTANT.FAA_APPROVAL_PENDING_STATUS:
        case CONSTANT.FAA_APPROVAL_CLEAR_STATUS:
        case CONSTANT.FAA_APPROVAL_BLOCKED_STATUS:
            return true;
        default:
            return false;
    }
}

//checks to see if Flight can be Published
export function canPublishByState(state, laancOn) {
    switch (state) {
        case CONSTANT.DECONFLICTION_CLEAR_STATUS:
            return laancOn === "false";
        case CONSTANT.DECONFLICTION_CHECK_STATUS:
        case CONSTANT.DECONFLICTION_BLOCKED_STATUS:
        case CONSTANT.LAANC_REQUIRED_STATUS:
        case CONSTANT.LAANC_BLOCKED_STATUS:
        case CONSTANT.FAA_APPROVAL_CHECK_STATUS:
        case CONSTANT.FAA_APPROVAL_PENDING_STATUS:
        case CONSTANT.FAA_APPROVAL_BLOCKED_STATUS:
        case CONSTANT.LAANC_FURTHER_COORD_STATUS:
        case CONSTANT.LAANC_AUTO_APPROVED_STATUS:
            return false;
        case CONSTANT.LAANC_NOT_REQUIRED_STATUS:
        case CONSTANT.FAA_APPROVAL_CLEAR_STATUS:
            return true;
        default:
            return false;
    }
}

//checks to see if the Flight can be submitted to LAANC
export function canSubmitToLAANC(state, laancOn) {
    if (laancOn === "false") {
        return false;
    }

    switch (state) {
        case CONSTANT.LAANC_FURTHER_COORD_STATUS:
        case CONSTANT.LAANC_AUTO_APPROVED_STATUS:
            return true;
        default:
            return false;
    }
}

//checks to see if the Flight can be editted
export function canEditFlight(state) {
    switch (state) {
        case CONSTANT.FAA_APPROVAL_CHECK_STATUS:
        case CONSTANT.FAA_APPROVAL_PENDING_STATUS:
        case CONSTANT.FAA_APPROVAL_BLOCKED_STATUS:
        case CONSTANT.FAA_APPROVAL_CLEAR_STATUS:
            return false;
        default:
            return true;
    }
}

//checks if the LAANC Services have blocked the flight.
export function isFlightBlocked(state) {
    switch (state) {
        case CONSTANT.LAANC_REQUIRED_STATUS:
        case CONSTANT.LAANC_BLOCKED_STATUS:
        case CONSTANT.LAANC_FURTHER_COORD_STATUS:
        case CONSTANT.LAANC_AUTO_APPROVED_STATUS:
            return true;
        default:
            return false;
    }
}

function volumeHasStarted(volume) {
    const currTime = new Date().getTime();
    const startTime = new Date(volume.time_start).getTime();
    if (currTime > startTime) {
        return true;
    } else {
        return false;
    }
}

export function updateLaancVolumesAfterAcknowledge(operation) {
    if (operation && operation.laanc && operation.laanc.volumes) {
        operation.laanc.volumes.forEach((vol) => {
            if (vol.state === CONSTANT.LAANC_RESCINDED_AWAITING) {
                vol.state = CONSTANT.LAANC_RESCINDED_ACK;
            } else if (vol.state === CONSTANT.LAANC_INVALID_AWAITING) {
                //If the LAANC Volume has Started -> Invalid Awaiting goes to Closed.
                if (volumeHasStarted(vol)) {
                    vol.state = CONSTANT.LAANC_CLOSED;
                } else {
                    vol.state = CONSTANT.LAANC_INVALID_CANCEL;
                }
            } else if (vol.state !== CONSTANT.LAANC_NOT_SUBMITTED) {
                if (volumeHasStarted(vol)) {
                    vol.state = CONSTANT.LAANC_CLOSED;
                } else {
                    vol.state = CONSTANT.LAANC_OPERATOR_CANCEL;
                }
            }
        });
    }
    return operation;
}

export function userAccountHasFirstAndLastName(user) {
    if (
        Object.prototype.hasOwnProperty.call(user, "first_name") &&
        user.first_name.trim() !== "" &&
        Object.prototype.hasOwnProperty.call(user, "last_name") &&
        user.last_name.trim() !== ""
    ) {
        return true;
    } else {
        return false;
    }
}

export function isValidBase64(str) {
    const base64Pattern = /^(data:)([\w/+:.-]+)?;base64,([A-Za-z0-9+/=]+)$/;
    if (base64Pattern.test(str)) {
        return true;
    } else {
        return false;
    }
}

export function isFurtherCoordination(volumes) {
    let isFC = false;
    volumes.forEach((vol) => {
        if (vol.faa_request_type == "FURTHER_COORDINATION") {
            isFC = true;
        }
    });

    return isFC;
}

export function getWaypointsDownloadable(operation, user) {
    if (operation.waypoints && operation.waypoints.length > 0) {
        if (user.user_role === "Admin") {
            return true;
        } else if (user.user_role === "Operator") {
            return operation.created_user_id === user.id;
        } else if (user.user_role === "First Responder") {
            return false;
        } else {
            return operation.organization_id === user.organization_id;
        }
    } else {
        return false;
    }
}

export function flightIsntLongEnough(startDate, endDate) {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    return IsInvalidDate(startDate, endDate) || end - start <= 61 * 1000;
}

export function flightIsTooLong(startDate, endDate) {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    return IsInvalidDate(startDate, endDate) || (end - start) / 1000 / 60 / 60 > 12;
}

export function validateFlightTimes(flightStart, flightEnd, isPublishedFlight = false, setSnackbar = undefined) {
    let snackbar = "";
    if (IsInvalidDate(flightStart, flightEnd)) {
        snackbar = "Please enter valid times with an end time later than the start time.";
    } else if (flightIsntLongEnough(flightStart, flightEnd)) {
        snackbar = "Please enter times with a duration longer than a minute.";
    } else if (flightIsTooLong(flightStart, flightEnd)) {
        snackbar = "The operation cannot be longer than 12 hours.";
    } else if (!isPublishedFlight && DateHasAlreadyPassed(flightStart)) {
        snackbar = "The entered start time has already passed. Please select another.";
    } else {
        return true;
    }

    if (setSnackbar) {
        setSnackbar({ children: snackbar, severity: "error" });
    }
    return false;
}

export function updateSafetyJustification(operation, safetyJustification) {
    if (operation.laanc && safetyJustification !== "") {
        operation.laanc.volumes.forEach((vol) => {
            if (vol.faa_request_type === "FURTHER_COORDINATION") {
                vol.safety_justification = safetyJustification;
            }
        });
    }
}

export function retrieveSafetyJustification(operation) {
    let justification = "";
    if (operation.laanc) {
        operation.laanc.volumes.forEach((vol) => {
            if (vol.faa_request_type === "FURTHER_COORDINATION") {
                justification = vol.safety_justification;
            }
        });
    }
    return justification;
}

export function getMaxAltitudeFromWaypointsAndVerticalBuffer(waypoints, verticalBufferFt) {
    let maxAltitudeFt = Number.MIN_SAFE_INTEGER;
    waypoints.forEach((waypoint) => {
        if (waypoint.alt_agl_ft > maxAltitudeFt) {
            maxAltitudeFt = waypoint.alt_agl_ft;
        }
    });
    return parseFloat(maxAltitudeFt) + parseFloat(verticalBufferFt);
}

export function canCreateOrDeleteCoaVolumes(user, coa) {
    if (user.user_role === "Admin") {
        return true;
    }

    const isOpManagerOrAirspaceManager = user.user_role === "Operations Manager" || user.user_role === "Airspace Manager";
    if (!isOpManagerOrAirspaceManager) {
        return false;
    }

    // No COA volume provided, meaning it's a CREATE operation.
    if (!coa) {
        return true;
    }

    return user.organization_id === coa.organization_id;
}

export const FlightStatus = {
    ACCEPTED: {
        name: "ACCEPTED",
        confirmation: "Are you sure you want to Accept this operation?",
        transitions: ["ACCEPTED", "ACTIVATED", "CONTINGENT", "NONCONFORMING", "ENDED"],
        deleting: false
    },
    ACTIVATED: {
        name: "ACTIVATED",
        confirmation: "Are you sure you want to Activate this operation?",
        transitions: ["ACTIVATED", "CONTINGENT", "NONCONFORMING", "ENDED"],
        deleting: false
    },
    CONTINGENT: {
        name: "CONTINGENT",
        confirmation: "Are you sure you want to change the status of this operation to Contingent?",
        transitions: ["CONTINGENT", "ENDED"],
        deleting: false
    },
    NONCONFORMING: {
        name: "NONCONFORMING",
        confirmation: "Are you sure you want to change the status of this operation to Nonconforming?",
        transitions: ["ACTIVATED", "CONTINGENT", "NONCONFORMING", "ENDED"],
        deleting: false
    },
    ENDED: {
        name: "ENDED",
        confirmation: "Are you sure you want to End this operation?",
        transitions: ["ENDED"],
        deleting: true
    },
    DELETED: {
        name: "DELETED",
        confirmation: "Are you sure you want to Delete this operation?",
        transitions: ["DELETED"],
        deleting: true
    },
    UNKNOWN: {
        name: "UNKNOWN",
        confirmation: null,
        transitions: [],
        deleting: false
    }
};

export function getFlightStatus(statusName) {
    const statusFormatted = statusName ? statusName.toUpperCase() : null;
    return statusFormatted && statusFormatted in FlightStatus ? FlightStatus[statusFormatted] : FlightStatus.UNKNOWN;
}

export function getFlightTransitions(flight) {
    return flight.conflicted ? [flight.state, "ENDED"] : getFlightStatus(flight.state).transitions;
}

export function handleFlightStatusChange(flight, statusName, user, handleFailedFetch) {
    const status = getFlightStatus(statusName);
    if (status === FlightStatus.UNKNOWN || !confirm(status.confirmation)) {
        return false;
    }

    const updatedFlight = {
        ...flight,
        state: status.name,
        updated_user_id: user.id,
        version: status.deleting ? flight.version : parseInt(flight.version) + 1
    };

    const url = status.deleting ? "/api/op/delete" : "/api/op/updatePublish";
    const requestOptions = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedFlight)
    };
    fetch(url, requestOptions)
        .then((response) => (response.ok ? response.json() : Promise.reject(response)))
        .catch((err) => handleFailedFetch(err));
    return true;
}

// Special flight status function to re-pull when fetch is completed.
export async function handleFlightStatusChangePromise(flight, statusName, user, handleFailedFetch) {
    const status = getFlightStatus(statusName);
    if (status === FlightStatus.UNKNOWN || !confirm(status.confirmation)) {
        return false;
    }

    const updatedFlight = {
        ...flight,
        state: status.name,
        updated_user_id: user.id,
        version: status.deleting ? flight.version : parseInt(flight.version) + 1
    };

    const url = status.deleting ? "/api/op/delete" : "/api/op/updatePublish";
    const requestOptions = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedFlight)
    };
    return fetch(url, requestOptions)
        .then((response) => (response.ok ? response.json() : Promise.reject(response)))
        .catch((err) => handleFailedFetch(err));
}

export function getExternalFlightStyle(flight, settings) {
    try {
        let externalFlightState = flight.state.toUpperCase();
        let externalFlightColor = "#3E436F";

        const setting = settings.op_state_settings.find((setting) => {
            return setting.state === externalFlightState;
        });
        if (setting) {
            externalFlightColor = setting.color;
        }
        return new Style({
            stroke: new Stroke({ color: externalFlightColor, width: 2, lineDash: [5, 5] }),
            fill: new Fill({ color: convertHexColorToRgbaColor(externalFlightColor, settings.op_opacity) })
        });
    } catch (err) {
        console.error(err);
        return undefined;
    }
}

export function getFeaturesFromExternalOperationMessages(messages, settings, timerange = null) {
    try {
        const features = [];

        let selectedStartTime = null;
        let selectedEndTime = null;

        if (Array.isArray(timerange) && timerange.length === 2) {
            selectedStartTime = typeof timerange[0].valueOf === "function" ? timerange[0].valueOf() : new Date(timerange[0]).getTime();
            selectedEndTime = typeof timerange[1].valueOf === "function" ? timerange[1].valueOf() : new Date(timerange[1]).getTime();
        }
        messages.forEach((message) => {
            message.volumes.forEach((volume) => {
                if (selectedStartTime !== null && selectedEndTime !== null) {
                    const volStart = new Date(volume.time_start).getTime();
                    const volEnd = new Date(volume.time_end).getTime();

                    const isOverlapping = volStart <= selectedEndTime && volEnd >= selectedStartTime;
                    if (!isOverlapping) return;
                }
                let geometry = null;

                if (volume.type === "Polygon") {
                    geometry = new Polygon([volume.vertices]);
                } else if (volume.type === "Circle") {
                    geometry = circular(volume.center, volume.radius_m, 128);
                }
                if (geometry) {
                    const newFeature = new Feature({
                        geometry: geometry
                    });
                    features.push(newFeature);

                    const style = getExternalFlightStyle(message, settings);
                    newFeature.setStyle(style);

                    const externalFlightVolume = {
                        state: message.state,
                        priority: message.priority,
                        altitude_min_hae_m: volume.altitude_min_hae_m,
                        altitude_max_hae_m: volume.altitude_max_hae_m,
                        time_start: volume.time_start,
                        time_end: volume.time_end
                    };
                    newFeature.setProperties({
                        name: `External Flight Volume`,
                        externalFlight: externalFlightVolume
                    });
                }
            });
        });
        return features;
    } catch (err) {
        console.log(err);
        return [];
    }
}

export async function getExternalOperations(organizationId) {
    try {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        const response = await fetch(`api/externalOperations/get/${organizationId}`, requestOptions);
        if (!response.ok) throw new Error(`Server responded with ${response.statusText}`);

        const operations = await response.json();
        return operations;
    } catch (err) {
        console.error("An error occured while fetching external operations", err);
        return [];
    }
}
